import { useState, type FC } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { DEFAULT_PAGE_SIZE } from '../../../common/constants'
import { Button } from '../../../components/Button/Button'
import { CustomPaginationComponent } from '../../../components/DataTable/CustomPaginationComponent'
import { Spinner } from '../../../components/Spinner/Spinner'
import { BookingStatus, type Booking, type SchedulingFilters } from '../../../models/Scheduling'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  useAcceptBookingMutation,
  useArchiveBookingMutation,
  useCancelBookingMutation,
  useGetBookingsQuery,
  useGetSchedulingEventTypesQuery,
  useGetSchedulingLocationsQuery,
} from '../../../store/scheduling/scheduling'
import { addToast } from '../../../store/toast/toastSlice'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { BookingActionMenu } from '../rows/BookingActionMenu'
import { BookingRow } from '../rows/BookingRow'
import { BookingTimeBlock } from '../rows/BookingTimeBlock'
import { ArchiveBookingDialog } from '../shared/ArchiveBookingDialog'
import { BookingReasonDialog } from '../shared/BookingReasonDialog'
import { reschedulePath } from '../shared/bookingNavigation'
import { SchedulingEmptyState } from './SchedulingEmptyState'
import { SchedulingTabErrorCard } from './SchedulingTabErrorCard'
import { toBookingsFilterArgs } from '../bookingTabs'

const requestSortTime = (booking: Booking): number => (booking.start ? new Date(booking.start).getTime() : Number.MAX_SAFE_INTEGER)

const isRequestExpired = (booking: Booking): boolean => (booking.start ? new Date(booking.start).getTime() < Date.now() : false)

const isRescheduleRequest = (booking: Booking): boolean => Boolean(booking.originalStart)

const getFlowTypeFor = (booking: Booking): 'schedule' | 'reschedule' => (isRescheduleRequest(booking) ? 'reschedule' : 'schedule')

const declineBody = (booking: Booking): string =>
  isRescheduleRequest(booking)
    ? "We'll notify the person who requested this meeting to be rescheduled that you declined their request. Every other participant gets notified."
    : "We'll notify the person who requested this meeting that you declined their booking request. Every other participant gets notified."

export const RequestsTab: FC<{ filters: SchedulingFilters }> = ({ filters }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { trackingClient } = useTracking()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const {
    data: bookingsResponse,
    isFetching,
    isError,
    refetch,
  } = useGetBookingsQuery({
    ...toBookingsFilterArgs(filters),
    accountId: authenticatedUser.accountId,
    status: BookingStatus.REQUESTED,
    pageRequest: { page: page - 1, size: pageSize },
  })
  const bookings = bookingsResponse?.data
  const totalElements = bookingsResponse?.page.totalElements ?? 0
  const { data: eventTypes } = useGetSchedulingEventTypesQuery({ accountId: authenticatedUser.accountId })
  const { data: locations } = useGetSchedulingLocationsQuery({ accountId: authenticatedUser.accountId })
  const [acceptBooking, { isLoading: isAccepting, originalArgs }] = useAcceptBookingMutation()
  const [cancelBooking, { isLoading: isCancelling, originalArgs: cancelArgs }] = useCancelBookingMutation()
  const [archiveBooking, { isLoading: isArchiving, originalArgs: archiveArgs }] = useArchiveBookingMutation()
  const [bookingToDecline, setBookingToDecline] = useState<Booking | null>(null)
  const [bookingToArchive, setBookingToArchive] = useState<Booking | null>(null)

  if (isFetching) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }

  if (isError) {
    return <SchedulingTabErrorCard title="Couldn't load requests" onRetry={refetch} />
  }

  if (!Array.isArray(bookings) || bookings.length === 0) {
    return <SchedulingEmptyState title='No requests' subtitle='Booking and reschedule requests from clients appear here.' />
  }

  const eventTypeById = new Map((eventTypes ?? []).map((eventType) => [eventType.id, eventType]))
  const locationById = new Map((locations ?? []).map((location) => [location.id, location]))
  const requests = [...bookings].sort((a, b) => requestSortTime(a) - requestSortTime(b))

  const handleAccept = async (booking: Booking) => {
    const bookingId = booking.bookingId
    const result = await acceptBooking({ accountId: authenticatedUser.accountId, bookingId })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not accept the booking', messages: ['Please try again.'] }))
    } else {
      trackingClient.track({ eventName: TrackEvent.SCHEDULING_REQUEST_ACCEPTED, eventDetails: { bookingId, flow: getFlowTypeFor(booking) } })
      dispatch(addToast({ variant: 'success', title: 'Booking confirmed', messages: [`${booking.title ?? 'The booking'} moved to Scheduled.`] }))
    }
  }

  const handleDecline = async (reason: string) => {
    if (!bookingToDecline) {
      return
    }
    const bookingId = bookingToDecline.bookingId
    const result = await cancelBooking({ accountId: authenticatedUser.accountId, payload: { bookingId, reason: reason || undefined } })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not decline the request', messages: ['Please try again.'] }))
      return
    }
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_REQUEST_DECLINED, eventDetails: { bookingId, flow: getFlowTypeFor(bookingToDecline) } })
    dispatch(addToast({ variant: 'success', title: 'Request declined', messages: [`${bookingToDecline.title ?? 'The request'} was declined.`] }))
    setBookingToDecline(null)
  }

  const handleArchive = async () => {
    if (!bookingToArchive) {
      return
    }
    const bookingId = bookingToArchive.bookingId
    const result = await archiveBooking({ accountId: authenticatedUser.accountId, bookingId })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not archive the booking', messages: ['Please try again.'] }))
      return
    }
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_BOOKING_ARCHIVED, eventDetails: { bookingId, source: 'requests' } })
    dispatch(addToast({ variant: 'success', title: 'Booking archived', messages: [`${bookingToArchive.title ?? 'The booking'} was archived.`] }))
    setBookingToArchive(null)
  }

  return (
    <>
      <div className='d-flex flex-column gap-2'>
        {requests.map((booking) => {
          const expired = isRequestExpired(booking)
          return (
            <BookingRow
              key={booking.bookingId}
              booking={booking}
              eventType={booking.eventTypeId ? eventTypeById.get(booking.eventTypeId) : undefined}
              location={booking.location?.id ? locationById.get(booking.location.id) : undefined}
              timeBlock={expired ? <BookingTimeBlock booking={booking} expired /> : undefined}
            >
              {expired ? null : (
                <Button
                  variant='primaryGhost'
                  loading={isAccepting && originalArgs?.bookingId === booking.bookingId}
                  disabled={isAccepting}
                  onClick={() => handleAccept(booking)}
                >
                  <i className='bi bi-check-lg me-1' />
                  Accept
                </Button>
              )}
              <Button variant='grayButton' onClick={() => navigate(reschedulePath(booking.bookingId))}>
                Reschedule
              </Button>
              <BookingActionMenu ariaLabel={`${booking.title ?? 'Booking'} actions`}>
                {expired ? (
                  <Dropdown.Item onClick={() => setBookingToArchive(booking)}>
                    <i className='bi bi-archive me-1' /> Archive
                  </Dropdown.Item>
                ) : (
                  <Dropdown.Item className='text-danger' onClick={() => setBookingToDecline(booking)}>
                    <i className='bi bi-slash-circle me-1' /> Decline request
                  </Dropdown.Item>
                )}
              </BookingActionMenu>
            </BookingRow>
          )
        })}
      </div>
      <CustomPaginationComponent
        rowsPerPage={pageSize}
        rowCount={totalElements}
        currentPage={page}
        onChangePage={setPage}
        onChangeRowsPerPage={(newPageSize) => {
          setPage(1)
          setPageSize(newPageSize)
        }}
      />
      {bookingToDecline ? (
        <BookingReasonDialog
          title={isRescheduleRequest(bookingToDecline) ? 'Declining reschedule request?' : 'Decline this booking request?'}
          body={declineBody(bookingToDecline)}
          confirmLabel='Decline request'
          isLoading={isCancelling && cancelArgs?.payload.bookingId === bookingToDecline.bookingId}
          onConfirm={handleDecline}
          onCancel={() => setBookingToDecline(null)}
        />
      ) : null}
      {bookingToArchive ? (
        <ArchiveBookingDialog
          isLoading={isArchiving && archiveArgs?.bookingId === bookingToArchive.bookingId}
          onConfirm={handleArchive}
          onCancel={() => setBookingToArchive(null)}
        />
      ) : null}
    </>
  )
}
